import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
    usePropertyPipelineConfig,
    usePipelineConfigSources,
    useSavePropertyPipelineConfig,
    fetchPropertyPipelineDefinition,
} from './usePipelineConfigApi'
import {
    fetchOrgLeadsPipelineDefinition,
    useOrgLeadsPipelineConfig,
    useSaveOrgLeadsPipelineConfig,
} from '../../../EzFolioConfigurations/useOrgPipelineConfigApi'
import {
    FlowEditTarget,
    EditStepsApplyPayload,
    PipelineDefinition,
    PipelineStepInstance,
    PipelineStepType,
    PipelineTransition,
    ValidationMessage,
} from './pipelineBuilder.types'
import {
    definitionFromApi,
    DEFAULT_PIPELINE_DEFINITION,
    ensureRequiredSteps,
} from './defaultDefinition'
import {
    catalogOrderIndex,
    getEnabledStepsInOrder,
    renumberEnabledBenchOrder,
    sortStepsByOrder,
} from './stepOrder'
import { createStepInstanceForDialog } from './pipelineBuilderStateUtils'
import { isRequiredStepType, isStepRepeatable } from './stepGuards'
import {
    applyTransitionToSteps,
    buildLinearTransitions,
    deriveTransition,
    transitionId,
} from './transitionUtils'
import { transitionConfigFromConversionRules } from './conversionRulesUtils'
import { transitionConfigFromAllMode } from './exitFromLeadsUtils'
import {
    isLeadsExitFromStep,
    isProspectsToLeadsTransition,
} from './transitionConfigUtils'
import { validateDefinition } from './validation'
import { cloneDefinition } from './emptyDefinition'

export type PipelineBuilderScope =
    | { scope: 'property'; apartmentId: number | null | undefined }
    | { scope: 'org'; organizationId: number | null | undefined }

export function usePipelineBuilderState(
    scopeConfig: PipelineBuilderScope,
    configId: number | null | undefined,
    readOnly = false,
) {
    const apartmentId =
        scopeConfig.scope === 'property' ? scopeConfig.apartmentId : null
    const organizationId =
        scopeConfig.scope === 'org' ? scopeConfig.organizationId : null

    const propertyConfigQuery = usePropertyPipelineConfig(apartmentId, configId)
    const orgConfigQuery = useOrgLeadsPipelineConfig(organizationId, configId)
    const configQuery =
        scopeConfig.scope === 'property' ? propertyConfigQuery : orgConfigQuery

    const sourcesQuery = usePipelineConfigSources(
        scopeConfig.scope === 'property' ? apartmentId : null,
        scopeConfig.scope === 'property' ? configId : null,
    )
    const savePropertyMutation = useSavePropertyPipelineConfig(
        apartmentId,
        configId,
    )
    const saveOrgMutation = useSaveOrgLeadsPipelineConfig(
        organizationId,
        configId,
    )

    const [definition, setDefinition] = useState<PipelineDefinition>(() =>
        ensureRequiredSteps(cloneDefinition(DEFAULT_PIPELINE_DEFINITION)),
    )
    const [editTarget, setEditTarget] = useState<FlowEditTarget>(null)
    const hydratedConfigId = useRef<number | null>(null)

    const effectiveReadOnly =
        readOnly || (configQuery.data != null && !configQuery.data.is_editable)

    useEffect(() => {
        if (configId == null) {
            hydratedConfigId.current = null
            setDefinition(
                ensureRequiredSteps(
                    cloneDefinition(DEFAULT_PIPELINE_DEFINITION),
                ),
            )
            setEditTarget(null)
            return
        }
        hydratedConfigId.current = null
        setDefinition(
            ensureRequiredSteps(cloneDefinition(DEFAULT_PIPELINE_DEFINITION)),
        )
        setEditTarget(null)
    }, [configId, apartmentId])

    useEffect(() => {
        if (configId == null) {
            return
        }
        if (configQuery.isLoading || configQuery.data === undefined) {
            return
        }
        if (hydratedConfigId.current === configId) {
            return
        }
        hydratedConfigId.current = configId
        setDefinition(definitionFromApi(configQuery.data.definition))
    }, [configId, configQuery.isLoading, configQuery.data])

    const persistDefinition = useCallback(
        (next: PipelineDefinition) => {
            if (effectiveReadOnly || configId == null) {
                return
            }
            if (scopeConfig.scope === 'property') {
                if (apartmentId == null) {
                    return
                }
                savePropertyMutation.mutate(next)
                return
            }
            if (organizationId == null) {
                return
            }
            saveOrgMutation.mutate({ definition: next })
        },
        [
            apartmentId,
            configId,
            effectiveReadOnly,
            organizationId,
            saveOrgMutation,
            savePropertyMutation,
            scopeConfig.scope,
        ],
    )

    const commitDefinition = useCallback(
        (
            updater:
                | PipelineDefinition
                | ((prev: PipelineDefinition) => PipelineDefinition),
        ) => {
            setDefinition((prev) => {
                const next =
                    typeof updater === 'function' ? updater(prev) : updater
                const ensured = ensureRequiredSteps(next)
                persistDefinition(ensured)
                return ensured
            })
        },
        [persistDefinition],
    )

    const validationMessages: ValidationMessage[] = useMemo(
        () => validateDefinition(definition),
        [definition],
    )

    const orderedSteps = useMemo(
        () =>
            sortStepsByOrder(
                Array.isArray(definition.steps) ? definition.steps : [],
            ),
        [definition.steps],
    )

    const enabledSteps = useMemo(
        () =>
            getEnabledStepsInOrder(
                Array.isArray(definition.steps) ? definition.steps : [],
            ),
        [definition.steps],
    )

    const transitions = useMemo(
        () =>
            buildLinearTransitions(enabledSteps, definition.transition_configs),
        [enabledSteps, definition.transition_configs],
    )

    const typesInPipeline = useMemo(
        () => new Set(definition.steps.map((s) => s.type)),
        [definition.steps],
    )

    const addStep = useCallback(
        (type: PipelineStepType) => {
            commitDefinition((prev) => {
                if (
                    !isStepRepeatable(type) &&
                    prev.steps.some((s) => s.type === type)
                ) {
                    return prev
                }
                const newStep = createStepInstanceForDialog(type, prev.steps)
                const enabled = getEnabledStepsInOrder(prev.steps)
                const typeCatalogIdx = catalogOrderIndex(type)
                let insertAt = enabled.length
                for (let i = 0; i < enabled.length; i += 1) {
                    if (catalogOrderIndex(enabled[i].type) > typeCatalogIdx) {
                        insertAt = i
                        break
                    }
                }
                const nextEnabled = [...enabled]
                nextEnabled.splice(insertAt, 0, newStep)
                return {
                    ...prev,
                    steps: renumberEnabledBenchOrder(nextEnabled, [
                        ...prev.steps,
                        newStep,
                    ]),
                }
            })
        },
        [commitDefinition],
    )

    const removeStep = useCallback(
        (stepId: string) => {
            commitDefinition((prev) => {
                const step = prev.steps.find((s) => s.id === stepId)
                if (!step || isRequiredStepType(step.type)) {
                    return prev
                }
                const remaining = prev.steps.filter((s) => s.id !== stepId)
                const enabled = getEnabledStepsInOrder(remaining)
                return {
                    ...prev,
                    steps: renumberEnabledBenchOrder(enabled, remaining),
                }
            })
            setEditTarget(null)
        },
        [commitDefinition],
    )

    const toggleStepEnabled = useCallback(
        (stepId: string) => {
            commitDefinition((prev) => {
                const nextSteps = prev.steps.map((step) => {
                    if (step.id !== stepId || isRequiredStepType(step.type)) {
                        return step
                    }
                    return { ...step, enabled: !step.enabled }
                })
                const enabled = getEnabledStepsInOrder(nextSteps)
                return {
                    ...prev,
                    steps: renumberEnabledBenchOrder(enabled, nextSteps),
                }
            })
        },
        [commitDefinition],
    )

    const updateStep = useCallback(
        (stepId: string, patch: Partial<PipelineStepInstance>) => {
            commitDefinition((prev) => {
                const nextSteps = prev.steps.map((step) =>
                    step.id === stepId ? { ...step, ...patch } : step,
                )
                const enabled = getEnabledStepsInOrder(nextSteps)
                return {
                    ...prev,
                    steps: renumberEnabledBenchOrder(enabled, nextSteps),
                }
            })
        },
        [commitDefinition],
    )

    const applyStepsEdit = useCallback(
        (payload: EditStepsApplyPayload) => {
            commitDefinition((prev) => {
                const removed = new Set(payload.removedStepIds)
                let steps = payload.allStepsOverride
                    ? payload.allStepsOverride.filter(
                          (s) =>
                              !removed.has(s.id) || isRequiredStepType(s.type),
                      )
                    : prev.steps.filter(
                          (s) =>
                              !removed.has(s.id) || isRequiredStepType(s.type),
                      )

                const benchIds = new Set(payload.benchSteps.map((s) => s.id))
                for (const benchStep of payload.benchSteps) {
                    const existing = steps.find((s) => s.id === benchStep.id)
                    if (existing) {
                        steps = steps.map((s) =>
                            s.id === benchStep.id ? { ...s, ...benchStep } : s,
                        )
                    } else {
                        steps = [...steps, benchStep]
                    }
                }

                steps = steps.map((s) => ({
                    ...s,
                    enabled: isRequiredStepType(s.type)
                        ? true
                        : benchIds.has(s.id),
                }))

                const benchSteps = payload.benchSteps.map(
                    (b) => steps.find((s) => s.id === b.id) ?? b,
                )

                return {
                    ...prev,
                    steps: renumberEnabledBenchOrder(benchSteps, steps),
                }
            })
            setEditTarget(null)
        },
        [commitDefinition],
    )

    const updateTransition = useCallback(
        (
            fromStepId: string,
            toStepId: string,
            transition: PipelineTransition,
        ) => {
            commitDefinition((prev) => {
                const fromStep = prev.steps.find((s) => s.id === fromStepId)
                const toStep = prev.steps.find((s) => s.id === toStepId)
                if (!fromStep || !toStep) {
                    return prev
                }
                const applied = applyTransitionToSteps(
                    fromStep,
                    toStep,
                    transition,
                )

                const key = transitionId(fromStepId, toStepId)
                const storedTransitionConfig = isProspectsToLeadsTransition(
                    fromStep,
                    toStep,
                )
                    ? transitionConfigFromConversionRules(
                          transition.conversionRules ?? [],
                          transition.reviewPrompt,
                      )
                    : isLeadsExitFromStep(fromStep)
                    ? transitionConfigFromAllMode(
                          transition.triggers,
                          transition.allowPmOverride ?? true,
                      )
                    : {
                          exit_mode: 'any' as const,
                          triggers: transition.triggers,
                          require_pm_approval:
                              transition.exitGate === 'pm_review',
                          review_prompt: transition.reviewPrompt,
                      }

                return {
                    ...prev,
                    steps: prev.steps.map((step) => {
                        if (step.id === fromStepId) {
                            return applied.fromStep
                        }
                        if (step.id === toStepId) {
                            return applied.toStep
                        }
                        return step
                    }),
                    transition_configs: {
                        ...prev.transition_configs,
                        [key]: storedTransitionConfig,
                    },
                }
            })
        },
        [commitDefinition],
    )

    const fetchConfigDefinition = useCallback(
        async (sourceApartmentId: number, sourceConfigId: number) => {
            if (scopeConfig.scope === 'org' && organizationId != null) {
                const loaded = await fetchOrgLeadsPipelineDefinition(
                    organizationId,
                    sourceConfigId,
                )
                return definitionFromApi(loaded)
            }
            const loaded = await fetchPropertyPipelineDefinition(
                sourceApartmentId,
                sourceConfigId,
            )
            return definitionFromApi(loaded)
        },
        [organizationId, scopeConfig.scope],
    )

    const openNode = useCallback((stepId: string) => {
        setEditTarget({ kind: 'node', stepId })
    }, [])

    const openEdge = useCallback((fromStepId: string, toStepId: string) => {
        setEditTarget({ kind: 'edge', fromStepId, toStepId })
    }, [])

    const closeEditor = useCallback(() => {
        setEditTarget(null)
    }, [])

    const editingStep = useMemo(() => {
        if (editTarget?.kind !== 'node') {
            return null
        }
        return definition.steps.find((s) => s.id === editTarget.stepId) ?? null
    }, [definition.steps, editTarget])

    const editingTransition = useMemo(() => {
        if (editTarget?.kind !== 'edge') {
            return null
        }
        const fromStep = definition.steps.find(
            (s) => s.id === editTarget.fromStepId,
        )
        const toStep = definition.steps.find(
            (s) => s.id === editTarget.toStepId,
        )
        if (!fromStep || !toStep) {
            return null
        }
        return deriveTransition(fromStep, toStep, definition.transition_configs)
    }, [definition.steps, definition.transition_configs, editTarget])

    const editingEdgeSteps = useMemo(() => {
        if (editTarget?.kind !== 'edge') {
            return null
        }
        const fromStep = definition.steps.find(
            (s) => s.id === editTarget.fromStepId,
        )
        const toStep = definition.steps.find(
            (s) => s.id === editTarget.toStepId,
        )
        if (!fromStep || !toStep) {
            return null
        }
        return { fromStep, toStep }
    }, [definition.steps, editTarget])

    return {
        apartmentId,
        configId,
        configName: configQuery.data?.name,
        definition,
        orderedSteps,
        enabledSteps,
        transitions,
        typesInPipeline,
        validationMessages,
        editTarget,
        editingStep,
        editingTransition,
        editingEdgeSteps,
        isLoading: configId != null && configQuery.isLoading,
        isSaving: savePropertyMutation.isLoading || saveOrgMutation.isLoading,
        isEditable: configQuery.data?.is_editable ?? true,
        previousConfigOptions:
            scopeConfig.scope === 'property' ? sourcesQuery.data ?? [] : [],
        addStep,
        removeStep,
        toggleStepEnabled,
        updateStep,
        applyStepsEdit,
        updateTransition,
        fetchConfigDefinition,
        openNode,
        openEdge,
        closeEditor,
    }
}
